import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from '../application/account/AuthProvider';
import { LocaleProvider } from '../application/i18n/LocaleProvider';
import { applyLocaleDocumentMetadata } from '../application/i18n/localeDocumentMetadata';
import { readBrowserLanguages, readStoredLocale } from '../application/i18n/localePreference';
import { JukeboxProvider } from '../application/sound/JukeboxProvider';
import { useAutomaticAppUpdate } from '../application/pwa/useAutomaticAppUpdate';
import { isSupportedLocale, splitLocalePath, stripLocaleLikePrefix } from '../domain/i18n/locales';
import { negotiateLocale } from '../domain/i18n/negotiateLocale';
import { translate } from '../domain/i18n/messages';
import AppErrorBoundary from '../ui/chrome/AppErrorBoundary';
import AppHeader from '../ui/chrome/AppHeader';
import BottomNavigation from '../ui/chrome/BottomNavigation';
import CarDetailScreen from '../ui/screens/CarDetailScreen';
import CompareScreen from '../ui/screens/CompareScreen';
import DuelScreen from '../ui/screens/DuelScreen';
import GarageScreen from '../ui/screens/GarageScreen';
import JukeboxScreen from '../ui/screens/JukeboxScreen';
import PlayScreen from '../ui/screens/PlayScreen';
import SplashScreen from '../ui/screens/SplashScreen';
import { ROUTE_PATHS, chromeForRoute } from './routes';

/**
 * The composition root's other half: what is mounted, under which locale, and
 * wearing which chrome.
 *
 * Everything about WHICH screen is data — `routes.js` owns the table and the
 * chrome rules, and this file only honours them. That is what keeps the second
 * tab, whenever it arrives, a row in that table rather than an edit here.
 */

/**
 * Send a visitor who arrived without a locale to the one we negotiated for them.
 * `replace` so the un-prefixed URL does not become a back-button trap.
 */
function LocaleRedirect() {
    const { pathname, search, hash } = useLocation();
    // Strips a locale-SHAPED prefix, so a link written for a language we have
    // not launched — `/de/cars/ferrari-f40` — keeps the car on the way to the
    // locale we negotiate for it, instead of dumping the reader on the splash.
    const routePath = stripLocaleLikePrefix(pathname);
    const localeCode = negotiateLocale({
        // A locale the reader chose themselves outranks any guess the browser's
        // list could support.
        storedLocale: readStoredLocale(),
        browserLanguages: readBrowserLanguages(),
    });
    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');

    return <Navigate to={`/${localeCode}${normalizedRoute}${search}${hash}`} replace />;
}

/**
 * Everything beneath `/:locale`. An unsupported locale segment is not a 404 — it
 * is most likely a language we have not shipped yet, so it re-negotiates rather
 * than dead-ends.
 */
function LocalisedShell() {
    const { locale } = useParams();
    const { pathname } = useLocation();

    // `<html lang>`, the document title and the canonical and hreflang links,
    // kept in step with the route. In an effect rather than in render because
    // all four are writes to a document outside React's tree.
    useEffect(() => {
        if (!isSupportedLocale(locale)) {
            return;
        }

        applyLocaleDocumentMetadata({ localeCode: locale, routePath: splitLocalePath(pathname).routePath });
    }, [locale, pathname]);

    if (!isSupportedLocale(locale)) {
        return <LocaleRedirect />;
    }

    return (
        <LocaleProvider localeCode={locale}>
            {/* INSIDE the locale provider, because the lock screen's "now
                playing" label is copy and copy comes from `t()` — and ABOVE the
                route table, because the element making the sound has to outlive
                every navigation. A locale change re-renders this position rather
                than remounting it, so switching language does not stop a track
                either (SPEC-014 D-2). */}
            <JukeboxProvider>
                <LocalisedChrome locale={locale} />
            </JukeboxProvider>
        </LocaleProvider>
    );
}

function LocalisedChrome({ locale }) {
    const { pathname } = useLocation();
    // Not every screen wears the same chrome: the splash is a brand moment with
    // none of it, and a car card is immersive with artwork to the top edge.
    const chrome = chromeForRoute(splitLocalePath(pathname).routePath);
    const shellClass = [
        'app-shell',
        chrome.immersive ? 'app-shell--immersive' : '',
        chrome.splash ? 'app-shell--splash' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={shellClass}>
            <div className="app-shell__content">
                {chrome.header ? <AppHeader /> : null}
                <Routes>
                    <Route index element={<SplashScreen />} />
                    <Route path="garage" element={<GarageScreen />} />
                    <Route path="duel" element={<DuelScreen />} />
                    <Route path="compare" element={<CompareScreen />} />
                    <Route path="play" element={<PlayScreen />} />
                    <Route path="jukebox" element={<JukeboxScreen />} />
                    <Route path="cars/:carId" element={<CarDetailScreen />} />
                    {/* Anything else under a good locale is a mistyped or
                        retired URL. It lands on the garage rather than the
                        splash: the splash is a moment, not a destination, and
                        sending a lost reader to an animation they then have to
                        sit through is a second wrong turn. */}
                    <Route path="*" element={<Navigate to={`/${locale}${ROUTE_PATHS.garage}`} replace />} />
                </Routes>
            </div>
            {chrome.tabBar ? <BottomNavigation /> : null}
        </div>
    );
}

export default function AppShell() {
    // The boundary sits ABOVE the locale provider, so its copy is resolved
    // against the browser's best guess rather than against the app's provider —
    // if the provider is what threw, there is no `t()` left to call.
    const fallbackLanguage = negotiateLocale({ browserLanguages: readBrowserLanguages() });

    // Above the routes, so the watch outlives every navigation: a reader who
    // walks the garage, a car and the duel is one page load, and restarting the
    // idle clock on each of them would mean a deploy that never lands while
    // anybody is actually using the app (SPEC-006 PR-6.5).
    //
    // `bootstrap.js` starts the LOOKING. This is what makes it consequential —
    // without it the app notices a new build and sits on it for ever.
    useAutomaticAppUpdate();

    return (
        <AppErrorBoundary
            title={translate(fallbackLanguage, 'error.title')}
            body={translate(fallbackLanguage, 'error.body')}
        >
            {/* ABOVE the locale routing, so a language switch does not remount
                the account and re-run its resolve — and INSIDE the boundary, so
                an auth failure that somehow reached render is a readable screen
                rather than a black one. It resolves to a guest on every failure
                path (SPEC-009 D-7), so nothing below it can be gated by it. */}
            <AuthProvider>
                <Routes>
                    <Route path="/:locale/*" element={<LocalisedShell />} />
                    <Route path="*" element={<LocaleRedirect />} />
                </Routes>
            </AuthProvider>
        </AppErrorBoundary>
    );
}
